import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Gantt } from '@bryntum/gantt';
import { useComponentI18n } from '@rdrive/i18n';
import { useFilterContextRefresh } from '@rdrive/data-filters';
import { useChartTheme } from '@rdrive/theme';
import messages from './messages/index.js';
import { DEFAULT_VIEW_PRESET, parseDate, viewPresetForDays } from '../../helpers/ganttDates.js';
import { applyBryntumTheme } from '../../helpers/bryntumTheme.js';

// The Bryntum wiring is issue-analysis's IssueDueByGantt.vue, line for line, minus its domain:
// the six SQL loaders are the app's `levels` + `dateRange`, the status list and its initial
// selection are the app's `statusOptions` + `selectedStatuses`, and the store read is the
// `filterContext` / `dataLoaded` props. Its debug `console.log`s (one per rendered row, the preset
// store dumps) are dropped; its `console.error`s stay.
//
// Five opt-ins (AM_ABSORPTION_PLAN step D6, for asset-management's inspection-schedule Gantt —
// standalone AM's gantts/InspectionScheduleGantt.vue): `columns`, `childPageSize` (+ the "load
// more" row), `restoreExpansion`, `ownFilterId` and `pinWidth`. Each is off by default, and every
// line it adds is behind its own prop (or acts on state only it fills: `loadMoreRows`,
// `expandedKeys`), so without them the Gantt config, the store calls, the loader calls, the
// listeners' effects and the emits are what they were — the README's "Opt-ins".

export default (props, { emit }) => {
  // LOCAL composer with the warn flags off (see @rdrive/i18n): the header, column and overlay
  // strings come from ./messages; `title` arrives translated.
  const { t } = useComponentI18n(messages);
  // The theme: its `mode` picks Bryntum's stylesheet (../../helpers/bryntumTheme.js — light =
  // Material, dark = Classic-Dark) and `gantt.leafRow` is the one colour the base sets itself
  // (the leaf rows' background)
  const theme = useChartTheme();
  // Dark mode tints Classic-Dark's grey surfaces with the dashboard's own: the `gantt` tokens'
  // surface entries become CSS variables on the container, read by ./style.css under
  // `.rdrive-gantt--tinted` (light's entries are null, so Material stays as shipped)
  const SURFACE_VARS = {
    background: '--rdrive-gantt-background',
    headerBackground: '--rdrive-gantt-header-background',
    splitter: '--rdrive-gantt-splitter',
    border: '--rdrive-gantt-border',
  };
  const surfaceStyle = computed(() => Object.fromEntries(
    Object.entries(SURFACE_VARS)
      .filter(([token]) => theme.value.gantt[token])
      .map(([token, cssVar]) => [cssVar, theme.value.gantt[token]])
  ));
  const tinted = computed(() => Object.keys(surfaceStyle.value).length > 0);
  // The container's style: the surface variables, then the box (the template's old static style,
  // in its order — Vue merged it after `surfaceStyle`). `pinWidth` (standalone AM's .gantt-container):
  // at most its column's width and never wider than it — the timeline scrolls inside Bryntum.
  const CONTAINER_BOX = {
    height: '600px', 'max-width': '95vw', width: '100%', position: 'relative', 'overflow-x': 'auto',
  };
  const PINNED_BOX = { 'max-width': '100%', 'overflow-x': 'hidden', 'min-width': '0', overflow: 'hidden' };
  const containerStyle = computed(() => (props.pinWidth
    ? { ...surfaceStyle.value, ...CONTAINER_BOX, ...PINNED_BOX }
    : { ...surfaceStyle.value, ...CONTAINER_BOX }));
  // The card: `ma-4` as before; `pinWidth` (AM's .gantt-card) drops the margin — the card fills its
  // column like the dashboard's other cards — and ./style.css pins it (min-width 0, max-width 100%,
  // overflow hidden), so Bryntum's natural timeline width cannot widen the page
  const cardClass = computed(() => (props.pinWidth ? 'rdrive-gantt-card--pinned' : 'ma-4'));

  const ganttContainer = ref(null);
  let gantt = null;
  let disposed = false;
  // Resolved once the Gantt exists (or failed to): every load waits for it — the first load can
  // be asked for (useFilterContextRefresh's onMounted) while the instance is still being built
  let markReady;
  const ganttReady = new Promise((resolve) => { markReady = resolve; });

  // Task ids are the base's: one counter per instance, so ids never collide across levels and
  // loads (the old SFC numbered the top 1..n per load and children from Date.now(), which two
  // loaders starting in the same millisecond shared). Each task's row (the loader's object +
  // its id) is kept here for the child loader and `taskSelected`.
  let nextId = 1;
  const rows = new Map();
  // Parents whose children are loading — beforeToggleNode and expandNode both fire on one expand
  const loadingStates = new Map();
  // `childPageSize`: the "load more" rows (task id → { parentId, level, offset }). Not in `rows`:
  // they are the base's, so `taskSelected` reports null for one, and they are never tinted.
  const loadMoreRows = new Map();
  // `restoreExpansion`: the `key`s of the rows the user has open, kept across reloads
  const expandedKeys = new Set();
  // `ownFilterId`: how many selections were reported since the last load — each one a write (or a
  // removal) of the app's own filter whose tick must not reload — and the OTHER filters as that
  // load saw them, so a tick that changed one of those still reloads
  let ownWrites = 0;
  let otherFiltersAtLoad = null;
  // Set while a reload clears the tree: Bryntum deselects the removed rows
  let clearing = false;
  // Latest load wins: a reload that starts while an older one awaits its rows drops the older
  // one's result (two overlapping loads used to append the top rows twice)
  let loadToken = 0;
  // Set when the first load starts; a selection change before it is the app's initial selection.
  // `loadedSelection` is the selection the latest load started with, so a change that reaches
  // the props together with `dataLoaded` loads once, whichever watcher runs first.
  let started = false;
  let loadedSelection = null;
  const isInitialLoad = ref(true);

  const showStatusPicker = computed(() => Array.isArray(props.statusOptions));

  // A loader row → a Bryntum task. A row with children is a lazy node (`children: true`, not
  // loaded, collapsed); a row without is a loaded leaf.
  const toTask = (row) => {
    const id = nextId++;
    rows.set(id, { ...row, id });
    const hasChildren = !!row.hasChildren;
    const task = {
      id,
      name: row.name,
      type: row.level,
      startDate: parseDate(row.startDate),
      endDate: parseDate(row.endDate),
      statusColor: row.color,
      statusTitle: row.statusTitle,
      hasChildren,
      loaded: !hasChildren,
      leaf: !hasChildren
    };
    // `columns`: each app column's row field, under a base-owned task field (so it sorts)
    for (const { taskField, field } of appColumnFields) task[taskField] = row[field];
    if (hasChildren) {
      task.children = true;
      task.expanded = false;
    }
    return task;
  };

  // ------------------------------------------------------------------ opt-in: columns
  // The app's columns (read once, at mount): a `type` is a built-in column with the app's header
  // and width; an entry without one is the app's own — its value is the row's `field` (copied onto
  // the task as `rdriveColumn<index>`, a name no Bryntum field has) and its cell is
  // `renderer(row, value)` (a string, which Bryntum escapes, or a DomConfig), where `row` is the
  // loader's object. A header is `text` as given, or `textKey` translated (the base's strings,
  // then the app's through the root composer).
  let appColumnFields = [];
  const BUILT_IN_WIDTHS = { name: 300, startdate: 130, enddate: 130 };
  const buildColumns = () => {
    if (!Array.isArray(props.columns)) {
      return [
        {
          type: 'name',
          field: 'name',
          text: t('name'),
          width: 300,
          renderer: ({ record }) => record.get('name')
        },
        { type: 'startdate', text: t('startDate'), width: 130 },
        { type: 'enddate', text: t('dueDate'), width: 130 }
      ];
    }
    appColumnFields = [];
    return props.columns.map((column, index) => {
      const text = column.textKey ? t(column.textKey) : (column.text ?? '');
      if (column.type === 'name') {
        return {
          type: 'name',
          field: 'name',
          text,
          width: column.width ?? BUILT_IN_WIDTHS.name,
          renderer: ({ record }) => record.get('name')
        };
      }
      if (column.type === 'startdate' || column.type === 'enddate') {
        return { type: column.type, text, width: column.width ?? BUILT_IN_WIDTHS[column.type] };
      }
      const taskField = `rdriveColumn${index}`;
      if (column.field) appColumnFields.push({ taskField, field: column.field });
      return {
        text,
        field: taskField,
        width: column.width ?? 130,
        ...(column.field ? {} : { sortable: false, filterable: false }),
        renderer: ({ record, value }) => {
          const row = rows.get(record.id);
          if (!row) return '';
          return column.renderer ? column.renderer(row, value) : (value ?? '');
        }
      };
    });
  };
  const taskFields = () => [
    'id', 'name', 'startDate', 'endDate', 'parentId', 'hasChildren', 'loaded', 'type', 'statusColor', 'statusTitle',
    ...appColumnFields.map(({ taskField }) => taskField)
  ];

  // ------------------------------------------------------------------ opt-in: child paging
  // `childPageSize[level]` (a positive integer) or 0: the children of a row of that level load
  // that many at a time
  const pageSizeFor = (level) => {
    const size = props.childPageSize ? Number(props.childPageSize[level]) : 0;
    return Number.isInteger(size) && size > 0 ? size : 0;
  };
  // A page's tasks, plus a "load more" row when the page came back full (standalone AM's rule)
  const withLoadMore = (pageRows, { parentId, level, offset, limit }) => {
    const tasks = pageRows.map(toTask);
    if (pageRows.length === limit) {
      const id = nextId++;
      loadMoreRows.set(id, { parentId, level, offset: offset + limit });
      tasks.push({
        id,
        name: t('loadMore'),
        type: 'rdrive-load-more',
        startDate: null,
        endDate: null,
        hasChildren: false,
        loaded: true,
        leaf: true
      });
    }
    return tasks;
  };

  // ------------------------------------------------------------------ opt-in: own filter
  // The filters other than the app's own, as one string (id, count, label, query each)
  const otherFilters = () => {
    const ctx = props.filterContext;
    const active = typeof ctx?.getActiveFilters === 'function' ? ctx.getActiveFilters() : [];
    return JSON.stringify(active
      .filter((filter) => filter.visualId !== props.ownFilterId)
      .map((filter) => [filter.visualId, filter.count, filter.label, ctx.filterQueries?.[filter.visualId] ?? null]));
  };
  const ownFilterRegistered = () => {
    const ctx = props.filterContext;
    const active = typeof ctx?.getActiveFilters === 'function' ? ctx.getActiveFilters() : [];
    return active.some((filter) => filter.visualId === props.ownFilterId);
  };

  // ------------------------------------------------------------------ opt-in: restore expansion
  const keyOf = (record) => {
    const key = rows.get(record?.id)?.key;
    return key === undefined || key === null ? null : key;
  };
  const trackExpansion = (record, expanded) => {
    if (!props.restoreExpansion) return;
    const key = keyOf(record);
    if (key === null) return;
    if (expanded) expandedKeys.add(key);
    else expandedKeys.delete(key);
  };

  // The app's date range → { startDate, endDate, viewPreset }, or null when there is none (no
  // rows, an unparsable date, an error) — the caller then falls back to DEFAULT_VIEW_PRESET
  const loadRange = async () => {
    try {
      const range = props.dateRange ? await props.dateRange() : null;
      if (!range || !range.start || !range.end) return null;

      const startParsed = parseDate(range.start);
      const endParsed = parseDate(range.end);
      const startDate = startParsed ? new Date(startParsed) : null;
      const endDate = endParsed ? new Date(endParsed) : null;
      if (!startDate || !endDate) {
        console.error('Invalid date range:', range);
        return null;
      }

      const durationMs = endDate.getTime() - startDate.getTime();
      const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
      return { startDate, endDate, viewPreset: viewPresetForDays(durationDays) };
    } catch (error) {
      console.error('Error determining the date range:', error);
      return null;
    }
  };

  // ------------------------------------------------------------------ lazy children
  const loadChildrenLazy = async (parentRecord) => {
    const parentId = parentRecord.id;
    const parentLevel = parentRecord.get('type') || 'unknown';

    if (loadingStates.get(parentId)) return [];
    if (parentRecord.get('loaded')) return [];

    loadingStates.set(parentId, true);
    try {
      const loader = props.levels[parentLevel];
      const limit = pageSizeFor(parentLevel);
      let children;
      if (loader && limit) {
        // `childPageSize`: the first page — `(parentRow, { offset, limit })`
        const page = await loader(rows.get(parentId), { offset: 0, limit });
        children = withLoadMore(page, { parentId, level: parentLevel, offset: 0, limit });
      } else {
        children = loader ? (await loader(rows.get(parentId))).map(toTask) : [];
      }
      loadingStates.set(parentId, false);
      return children;
    } catch (error) {
      console.error('Error loading children for parent:', parentId, error);
      loadingStates.set(parentId, false);
      return [];
    }
  };

  // Load a not-yet-loaded node's children and append them — what the three triggers below share
  const loadChildrenInto = async (record, source, { expand = false } = {}) => {
    if (!record || !record.get('hasChildren') || record.get('loaded')) return;
    try {
      const children = await loadChildrenLazy(record);
      if (children.length > 0) {
        record.appendChild(children);
        record.set('loaded', true);
        if (expand && gantt) gantt.project.taskStore.expand(record);
      }
    } catch (error) {
      console.error(`${source}: Error loading children:`, error);
    }
  };

  // `childPageSize`: a "load more" row was clicked — the next page replaces it (standalone AM: the
  // row is removed and the page, with a new "load more" row when it came back full, appended)
  const loadNextPage = async (record) => {
    const entry = loadMoreRows.get(record.id);
    if (!entry || loadingStates.get(record.id)) return;
    loadingStates.set(record.id, true);
    const token = loadToken;
    try {
      const loader = props.levels[entry.level];
      const parentRow = rows.get(entry.parentId);
      const limit = pageSizeFor(entry.level);
      const page = loader && parentRow && limit ? await loader(parentRow, { offset: entry.offset, limit }) : [];
      if (token !== loadToken || !gantt || !loadMoreRows.has(record.id)) return;
      const tasks = withLoadMore(page, { parentId: entry.parentId, level: entry.level, offset: entry.offset, limit });
      const parent = record.parent;
      loadMoreRows.delete(record.id);
      record.remove();
      if (tasks.length > 0 && parent) parent.appendChild(tasks);
    } catch (error) {
      console.error('Error loading more children:', error);
    } finally {
      loadingStates.delete(record.id);
    }
  };

  // `restoreExpansion`: after a reload, re-open the rows whose key the user had open — each
  // row's children loaded and expanded, then its own open children, and so on (rows a page did
  // not load stay closed). A newer reload stops it.
  const restoreOpenRows = async (records, token) => {
    for (const record of [...(records || [])]) {
      if (token !== loadToken || !gantt) return;
      const key = keyOf(record);
      if (key === null || !expandedKeys.has(key) || !record.get('hasChildren')) continue;
      await loadChildrenInto(record, 'restoreExpansion', { expand: true });
      if (token !== loadToken || !gantt) return;
      if (record.get('loaded')) await restoreOpenRows(record.children, token);
    }
  };

  // ------------------------------------------------------------------ the Gantt
  const initGantt = async () => {
    // Bryntum measures its rows and headers when it is built, so its stylesheet must be in first
    const [range] = await Promise.all([loadRange(), applyBryntumTheme(theme.value.mode)]);
    if (disposed) return;

    gantt = new Gantt({
      appendTo: ganttContainer.value,
      scrollTaskIntoViewOnCellClick: true,
      columns: buildColumns(),
      features: {
        taskEdit: false,
        taskDrag: false,
        taskResize: false,
        taskDragCreate: false,
        criticalPaths: false,
        progressLine: false,
        rollups: false,
        baselines: false,
        dependencies: false,
        rowReorder: false,
        filter: true,
      },
      viewPreset: range ? range.viewPreset : DEFAULT_VIEW_PRESET,
      barMargin: 5,
      rowHeight: 35,
      // A leaf's bar takes its row's colour (issue-analysis: the issue's status colour)
      taskRenderer({ taskRecord, renderData }) {
        if (taskRecord.isLeaf && taskRecord.get('statusColor')) {
          renderData.style = `background-color: ${taskRecord.get('statusColor')}`;
        }
      },
      project: {
        taskStore: {
          tree: true,
          fields: taskFields(),
          rootNode: {
            expanded: true,
            children: []
          }
        }
      },
      // The rows of a task without children (issue-analysis: the issues) are tinted
      onRenderRow: ({ row, record }) => {
        row.elements.locked.style.backgroundColor = '';
        row.elements.normal.style.backgroundColor = '';
        if (!record.get('hasChildren') && !loadMoreRows.has(record.id)) {
          row.elements.locked.style.backgroundColor = theme.value.gantt.leafRow;
          row.elements.normal.style.backgroundColor = theme.value.gantt.leafRow;
        }
      },
      listeners: {
        // Bryntum's event carries `collapse` (no `expanded`): every single expand or collapse,
        // the store's own `expand` included — what `restoreExpansion` tracks
        beforeToggleNode: async ({ record, expanded, collapse }) => {
          if (typeof collapse === 'boolean') trackExpansion(record, !collapse);
          if (expanded) await loadChildrenInto(record, 'beforeToggleNode');
        },
        // …and each node Expand All opens (its store events are suspended)
        expandNode: async ({ record }) => {
          trackExpansion(record, true);
          await loadChildrenInto(record, 'expandNode');
        },
        cellClick: async ({ record, column }) => {
          // `childPageSize`: a click anywhere on a "load more" row loads the next page
          if (loadMoreRows.has(record.id)) {
            await loadNextPage(record);
            return;
          }
          if (column.type === 'tree' || column.field === 'name') {
            await loadChildrenInto(record, 'cellClick', { expand: true });
          }
        },
        selectionChange: ({ selected, deselected }) => {
          if (selected.length > 0) {
            const row = rows.get(selected[0].id) ?? null;
            // `ownFilterId`: the app writes (a row) or removes (null, when registered) its filter
            if (props.ownFilterId && (row || ownFilterRegistered())) ownWrites += 1;
            emit('taskSelected', row);
          } else {
            if (props.ownFilterId) {
              // a reload's (or a replaced "load more" row's) deselection: the filter stays
              if (clearing || !(deselected || []).some((record) => rows.has(record.id))) return;
              if (ownFilterRegistered()) ownWrites += 1;
            }
            emit('taskSelected', null);
          }
        }
      }
    });
  };

  const expandAll = () => {
    if (gantt) {
      gantt.expandAll();
    }
  };

  const collapseAll = () => {
    // `restoreExpansion`: Collapse All forgets every open row (standalone AM)
    if (props.restoreExpansion) expandedKeys.clear();
    if (gantt) {
      gantt.collapseAll();
    }
  };

  const zoomIn = () => {
    if (gantt) {
      gantt.zoomIn();
    }
  };

  const zoomOut = () => {
    if (gantt) {
      gantt.zoomOut();
    }
  };

  // Fit the time axis to the app's date range: its span, its preset, scrolled to its start.
  // (The old SFC ran the range query twice here — once for the span, once for the preset.)
  const zoomToFit = async () => {
    if (!gantt) return;
    const range = await loadRange();
    if (!gantt) return;
    if (!range) {
      gantt.viewPreset = DEFAULT_VIEW_PRESET;
      return;
    }
    try {
      // Set the time span to cover the full range, then the preset for its length
      gantt.setTimeSpan(range.startDate, range.endDate);
      gantt.viewPreset = range.viewPreset;
      // Scroll to the start date, relying on the horizontal scrollbar for navigation
      await nextTick();
      if (gantt) gantt.scrollToDate(range.startDate, { block: 'start', animate: false });
    } catch (error) {
      console.error('Error zooming to the date range:', error);
      if (gantt) gantt.viewPreset = DEFAULT_VIEW_PRESET;
    }
  };

  // Reload from the top: clear the tree, load the root rows, fit the axis to them.
  // `overlay` shows the loading overlay meanwhile (a filter tick; a status change does not).
  const load = async ({ overlay = false } = {}) => {
    started = true;
    loadedSelection = props.selectedStatuses;
    if (overlay) isInitialLoad.value = true;
    await ganttReady;
    if (!gantt) return;

    const token = ++loadToken;
    if (props.ownFilterId) {
      ownWrites = 0;
      otherFiltersAtLoad = otherFilters();
    }
    clearing = true;
    try {
      gantt.project.taskStore.removeAll();
    } finally {
      clearing = false;
    }
    rows.clear();
    loadingStates.clear();
    loadMoreRows.clear();

    let rootRows = [];
    try {
      rootRows = await props.levels.root();
    } catch (error) {
      console.error('Error loading root level:', error);
    }
    if (token !== loadToken || !gantt) return;

    const rootTasks = rootRows.map(toTask);
    if (rootTasks.length > 0) {
      gantt.project.taskStore.rootNode.appendChild(rootTasks);
      await nextTick();
      await zoomToFit();
      if (props.restoreExpansion && expandedKeys.size > 0) {
        await restoreOpenRows(gantt?.project.taskStore.rootNode.children, token);
        if (token !== loadToken || !gantt) return;
      }
    }

    loadingStates.clear();
    isInitialLoad.value = false;
  };

  // ------------------------------------------------------------------ the status picker
  // The selection is the app's (v-model): the picker only emits; the reload follows the prop
  const handleStatusChange = (newStatuses) => {
    emit('update:selectedStatuses', newStatuses);
  };

  const removeStatus = (status) => {
    const index = props.selectedStatuses.findIndex((s) => s.title === status.title && s.color === status.color);
    if (index > -1) {
      emit('update:selectedStatuses', props.selectedStatuses.filter((_, i) => i !== index));
    }
  };

  watch(() => props.selectedStatuses, (selection) => {
    if (started && selection !== loadedSelection) load();
  });

  // ------------------------------------------------------------------ lifecycle
  // Built on mount (its first view preset from the date range), destroyed on unmount
  onMounted(async () => {
    await nextTick();
    try {
      await initGantt();
    } catch (error) {
      console.error('Error creating the Gantt:', error);
    } finally {
      markReady();
    }
  });

  onUnmounted(() => {
    disposed = true;
    if (gantt) {
      gantt.destroy();
      gantt = null;
    }
  });

  // Follow a live theme switch (the kebab toggle, the OS scheme): swap Bryntum's stylesheet, then
  // re-render the rows so `onRenderRow` paints the leaf rows in the new mode's `gantt.leafRow`
  watch(() => theme.value.mode, async (mode) => {
    await applyBryntumTheme(mode);
    if (gantt && !disposed) gantt.refreshRows();
  });

  // `ownFilterId`: the tick of the app's own write (a reported selection outstanding, and no OTHER
  // filter changed since the last load) keeps the tree — the selected row stays where it is
  const refresh = () => {
    if (props.ownFilterId && started && ownWrites > 0 && otherFilters() === otherFiltersAtLoad) {
      ownWrites -= 1;
      return;
    }
    load({ overlay: true });
  };

  // The first load once dataLoaded is true (on mount or when it flips), then a reload with the
  // overlay on every filter tick — registered after the onMounted above, as the composable asks
  useFilterContextRefresh(
    computed(() => props.filterContext),
    computed(() => props.dataLoaded),
    refresh,
    { debounce: 0, immediate: true, requireDataLoaded: true }
  );

  return {
    t,
    // `ganttContainer` is a STRING template ref (`ref="…"` in component.vue) — it only binds
    // because it is returned under that exact name
    ganttContainer,
    cardClass,
    containerStyle,
    tinted,
    isInitialLoad,
    showStatusPicker,
    handleStatusChange,
    removeStatus,
    expandAll,
    collapseAll,
    zoomIn,
    zoomOut,
    zoomToFit,
  };
};
